import Cookies from 'js-cookie';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { EXTENDED_SEGMENTATION_COOKIE } from '@/utils/segmentationTestHelper';
import { Briefcase } from '../icons/Briefcase';
import { CorporateClothing } from '../icons/CorporateClothing';
import { Education } from '../icons/Education';
import { Gift } from '../icons/Gift';
import { Handshake } from '../icons/Handshake';
import { MoreHorizontal } from '../icons/MoreHorizontal';
import { Resell } from '../icons/Resell';
import { Trophy } from '../icons/Trophy';
import { User as UserIcon } from '../icons/User';
import { Users as UsersIcon } from '../icons/Users';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '../modal/modal';
import './SegmentationTestModal.scss';

type VoidFn = () => void;
type SelectModalProps = Record<string, VoidFn>;
type ExtendedSelectProps = {
  type:
    | 'gift'
    | 'resell'
    | 'personal'
    | 'privateEvent'
    | 'educational'
    | 'corporateEvent'
    | 'sportsClub'
    | 'corporateClothing'
    | 'promotional'
    | 'other';
  optIn: VoidFn;
};

const EXTENDED_TRACKING_NAME = 'extended-segmentation-test';

const ExtendedSegmentationSelectModal = ({ onClose, onClick }: SelectModalProps) => {
  const optIn = (type: string) => {
    Cookies.set(EXTENDED_SEGMENTATION_COOKIE, 'true', { expires: 60 });
    trackFeature(EXTENDED_TRACKING_NAME, { action: `optIn-${type}` });
    onClick();
  };
  const optOutAndClose = () => {
    Cookies.set(EXTENDED_SEGMENTATION_COOKIE, 'true', { expires: 60 });
    trackFeature(EXTENDED_TRACKING_NAME, { action: 'optOut' });
    onClose();
  };

  return (
    <Modal title="extended-segmentation-test" className="segmentation-modal">
      <ModalHeader>
        <ModalTitle>{t('extendedSegmentation.headline')}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="segmentation-modal__extended">
          <ExtendedSegmentationSelect type="gift" optIn={() => optIn('gift')} />
          <ExtendedSegmentationSelect type="resell" optIn={() => optIn('resell')} />
          <ExtendedSegmentationSelect type="personal" optIn={() => optIn('personal')} />
          <ExtendedSegmentationSelect type="privateEvent" optIn={() => optIn('privateEvent')} />
          <ExtendedSegmentationSelect type="educational" optIn={() => optIn('educational')} />
          <ExtendedSegmentationSelect type="corporateEvent" optIn={() => optIn('corporateEvent')} />
          <ExtendedSegmentationSelect type="sportsClub" optIn={() => optIn('sportsClub')} />
          <ExtendedSegmentationSelect
            type="corporateClothing"
            optIn={() => optIn('corporateClothing')}
          />
          <ExtendedSegmentationSelect type="promotional" optIn={() => optIn('promotional')} />
          <ExtendedSegmentationSelect type="other" optIn={() => optIn('other')} />
        </div>
        <button onClick={optOutAndClose} className="segmentation-modal__optOut underline">
          {t('extendedSegmentation.optOut')}
        </button>
        <p className="segmentation-modal__subheading">
          {t('extendedSegmentation.subheading')}{' '}
          <a
            className="segmentation-modal__subheading--underline"
            href={t('segmentation.select.policyHref')}
            target="_blank"
            rel="noreferrer"
          >
            {t('segmentation.select.policyText')}
          </a>
          .
        </p>
      </ModalContent>
    </Modal>
  );
};

const ExtendedSegmentationSelect = ({ type, optIn }: ExtendedSelectProps) => {
  return (
    <button onClick={optIn} className="segmentation-modal__select">
      {type === 'gift' && <Gift className="segmentation-modal__icon" />}
      {type === 'resell' && <Resell className="segmentation-modal__icon" />}
      {type === 'personal' && <UserIcon className="segmentation-modal__icon" />}
      {type === 'privateEvent' && <UsersIcon className="segmentation-modal__icon" />}
      {type === 'educational' && <Education className="segmentation-modal__icon" />}
      {type === 'corporateEvent' && <Handshake className="segmentation-modal__icon" />}
      {type === 'sportsClub' && <Trophy className="segmentation-modal__icon" />}
      {type === 'corporateClothing' && <CorporateClothing className="segmentation-modal__icon" />}
      {type === 'promotional' && <Briefcase className="segmentation-modal__icon" />}
      {type === 'other' && <MoreHorizontal className="segmentation-modal__icon" />}
      <p className="segmentation-modal__description">{t(`extendedSegmentation.${type}`)}</p>
    </button>
  );
};

export const SegmentationTest = ({ onClose }: { onClose: VoidFn }) => {
  return <ExtendedSegmentationSelectModal onClose={onClose} onClick={onClose} />;
};
